import { useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import { createPalette, PaletteCommandType, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { OrganizationIcon } from '../../Components';
import { useNavigation } from '../../navigation';
import { apiResource, readAPICache, useAuthOperations, userThemeAtom } from '../../state';

/**
 * Settings palette accessible from the sidebar header.
 */
export function useGetSettingsPalette(props: { organization: api.Organization }) {
    const { organization } = props;
    const { signOut } = useAuthOperations();
    const { navigate } = useNavigation();
    const [userTheme, setUserTheme] = useRecoilState(userThemeAtom);
    const getOrganizationListPaletteGroups = useGetOrganizationListPaletteGroups();
    return useEventCallback(() => {
        const groups: PaletteGroupDef[] = [];

        const switchTheme = (theme: UserTheme) => {
            setUserTheme(theme);
            analytics.logEvent({
                type: 'account_edit_theme',
                theme,
            });
        };

        groups.push({
            key: 'org_settings',
            commands: [
                {
                    key: 'org_settings.profile',
                    title: 'Settings',
                    icon: Icon.Settings,
                    trigger: () => {
                        navigate({
                            overlay: {
                                type: 'org_settings',
                                organization: organization.id,
                                tab: { type: 'profile' },
                            },
                        });
                    },
                },
                organization.permissions.admin
                    ? {
                          key: 'org_settings.invite',
                          title: 'Invite your team',
                          icon: Icon.UserAdd,
                          trigger: () => {
                              navigate({
                                  overlay: {
                                      type: 'org_invite',
                                      organization: organization.id,
                                  },
                              });
                          },
                      }
                    : null,
            ].filter((x) => x !== null),
        });

        groups.push({
            key: 'personal_settings',
            commands: [
                {
                    key: 'personal_settings.switch',
                    icon: Icon.Modes,
                    title: 'Theme',
                    descendant: () =>
                        createPalette({
                            static: true,
                            groups: [
                                {
                                    key: 'theme',
                                    commands: [
                                        {
                                            key: 'theme.default',
                                            title: 'System',
                                            icon: Icon.DeviceDesktop,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Default,
                                            trigger: () => switchTheme(UserTheme.Default),
                                        },
                                        {
                                            key: 'theme.light',
                                            title: 'Light',
                                            icon: Icon.ThemeLight,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Light,
                                            trigger: () => switchTheme(UserTheme.Light),
                                        },
                                        {
                                            key: 'theme.dark',
                                            title: 'Dark',
                                            icon: Icon.ThemeDark,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Dark,
                                            trigger: () => switchTheme(UserTheme.Dark),
                                        },
                                    ],
                                },
                            ],
                        }),
                },
            ],
        });

        groups.push({
            key: 'account',
            commands: [
                {
                    key: 'switch-organization',
                    icon: Icon.Organization,
                    title: 'Switch organization',
                    descendant: () =>
                        createPalette({
                            groups: getOrganizationListPaletteGroups({
                                organizationId: organization.id,
                            }),
                        }),
                },
                {
                    key: 'account.signout',
                    icon: Icon.Leave,
                    title: 'Sign out',
                    trigger: () => {
                        signOut();
                    },
                },
            ],
        });

        return createPalette({ groups });
    });
}

function useGetOrganizationListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback((props: { organizationId: string }) => {
        const { organizationId } = props;
        return [
            {
                key: 'organizations',
                commands: () => {
                    const organizations = readAPICache(
                        apiResource('listOrganizationsForAuthenticatedUser', { limit: 100 })
                    );

                    return [
                        ...organizations.items.map((org) => {
                            return {
                                key: org.id,
                                title: org.title,
                                ...(org.id === organizationId
                                    ? {
                                          type: PaletteCommandType.RADIO as const,
                                          selected: true,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                                icon: <OrganizationIcon organization={org} />,
                                trigger: () => {
                                    navigate({
                                        screen: {
                                            type: 'home',
                                            organization: org.id,
                                        },
                                    });
                                },
                            };
                        }),
                        {
                            key: 'create-organization',
                            title: 'Create an organization',
                            icon: Icon.Organization,
                            trigger: () => {
                                analytics.logEvent({
                                    type: 'organization_open_create',
                                    reason: 'sidebar_menu',
                                });

                                navigate({
                                    overlay: {
                                        type: 'welcome',
                                        organizationType: 'business',
                                        hash: 'sidebar_menu',
                                    },
                                });
                            },
                        },
                    ];
                },
            },
        ];
    });
}
